import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Truck, Shield, Clock, CheckCircle, MapPin } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useLanguage } from '@/lib/useLanguage';
import { COUNTY_TO_DISTRICT } from '@/components/counties/countyDistricts';
import { countyContent, COUNTY_EQUIPMENT } from '@/components/seo/countyI18n';
import { countyUrl, equipmentUrl, districtUrl, LANG_ROUTES, PATH_LANGS } from '@/components/seo/langRoutes';
import { rentalCountyUrl } from '@/components/seo/crossDomainHreflang';
import { siteBaseUrl, toSitePath } from '@/lib/siteDomain';
import { brandName, brandShort } from '@/components/seo/brandI18n';
import OrthocareFooter from '@/components/layout/OrthocareFooter';
import RealReviewsSection from '@/components/seo/RealReviewsSection';
import OrthocareHeroSearch from '@/components/landing/OrthocareHeroSearch';
import ComplaintsBookButton from '@/components/layout/ComplaintsBookButton';
import { useNoIndexMeta } from '@/hooks/useNoIndexMeta';

const slugify = (text) =>
  (text || '').toString().toLowerCase()
    .replace(/[àáâãäå]/g, 'a').replace(/[èéêë]/g, 'e').replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o').replace(/[ùúûü]/g, 'u').replace(/[ç]/g, 'c')
    .replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-');

// Detect the active language + county slug from the path. Each language has its
// own county segment word (concelho / county / commune / kreis / comune).
function detectCountyRoute(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  // PT: /aluguer/concelho/:county
  if (segments[0] === 'aluguer' && segments[1] === LANG_ROUTES.pt.countyWord) {
    return { lang: 'pt', countySlug: segments[2] || '' };
  }
  // Localized: /<seg>/<rentWord>/<countyWord>/:county
  for (const l of PATH_LANGS) {
    const cfg = LANG_ROUTES[l];
    if (segments[0] === cfg.seg && segments[1] === cfg.rentWord && segments[2] === cfg.countyWord) {
      return { lang: l, countySlug: segments[3] || '' };
    }
  }
  return { lang: 'pt', countySlug: segments[segments.length - 1] || '' };
}

export default function CountySEOPage() {
  const { lang, countySlug } = detectCountyRoute(window.location.pathname);
  const { lang: uiLang } = useLanguage();

  const { data: counties = [] } = useQuery({
    queryKey: ['seo-counties-all'],
    // Only `name` is used (slug matching). Projecting the fields stops this
    // high-traffic public page from reading every county description.
    queryFn: async () => {
      const page = await base44.entities.County.filter({ active: true }, { limit: 3000, fields: ['name'] });
      return page?.items || page || [];
    },
    staleTime: 1000 * 60 * 30,
  });

  const county = useMemo(() => {
    if (!counties.length) return null;
    const s = slugify(countySlug);
    return counties.find((c) => slugify(c.name) === s)
      || counties.find((c) => slugify(c.name) === slugify((countySlug || '').replace(/-/g, ' ')))
      || null;
  }, [counties, countySlug]);

  const district = county ? (COUNTY_TO_DISTRICT[county.name] || '') : '';
  const c = county ? countyContent(lang, county.name, district) : null;

  useEffect(() => {
    if (!county || !c) return;
    document.title = `${c.h1} | ${brandName()}`;
    setMeta('description', c.subtitle);
    const cSlug = slugify(county.name);
    const langUrls = {
      pt: rentalCountyUrl('pt', cSlug), en: rentalCountyUrl('en', cSlug), es: rentalCountyUrl('es', cSlug),
      fr: rentalCountyUrl('fr', cSlug), de: rentalCountyUrl('de', cSlug), it: rentalCountyUrl('it', cSlug),
      nl: rentalCountyUrl('nl', cSlug),
    };
    setCanonical(langUrls[lang] || langUrls.pt);
    setHreflang(langUrls);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', langUrls[lang] || langUrls.pt);
    const siteBase = siteBaseUrl();
    const faq = c.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } }));
    const itemList = COUNTY_EQUIPMENT.map((eq, i) => ({
      '@type': 'ListItem', position: i + 1, name: eq.labels[lang] || eq.labels.pt,
      url: `${siteBase}${toSitePath(equipmentUrl(lang, eq.slug, cSlug))}`,
    }));
    injectSchema('county-medical', {
      '@context': 'https://schema.org', '@type': 'MedicalBusiness',
      name: `${brandName()} — ${county.name}`,
      areaServed: { '@type': 'AdministrativeArea', name: county.name },
      telephone: '+351216035281',
      address: { '@type': 'PostalAddress', addressCountry: 'PT' },
      image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/7526512f2_file_000000000f3871f4acdc11aca9b1a76e.png',
      '@id': langUrls[lang] || langUrls.pt, url: langUrls[lang] || langUrls.pt,
    });
    injectSchema('county-faq', { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: faq });
    injectSchema('county-itemlist', { '@context': 'https://schema.org', '@type': 'ItemList', itemListElement: itemList });
    injectSchema('county-breadcrumb', {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: brandShort(), item: `${siteBase}/` },
        ...(district ? [{ '@type': 'ListItem', position: 2, name: district, item: `${siteBase}${toSitePath(districtUrl(lang, slugify(district)))}` }] : []),
        { '@type': 'ListItem', position: district ? 3 : 2, name: county.name, item: langUrls[lang] || langUrls.pt },
      ],
    });
    setMeta('geo.region', 'PT');
    setMeta('geo.placename', county.name);
    document.documentElement.lang = lang;
    return () => {
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
      ['county-medical', 'county-faq', 'county-itemlist', 'county-breadcrumb'].forEach((id) => document.getElementById(id)?.remove());
    };
  }, [county, lang, c]);

  const notFound = counties.length > 0 && (!county || !c);
  useNoIndexMeta(notFound);

  if (!counties.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!county || !c) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="text-center max-w-md">
          <MapPin className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">{lang === 'pt' ? 'Concelho não encontrado' : 'County not found'}</h1>
          <Link to="/" className="text-[#0a3452] font-semibold hover:underline">{lang === 'pt' ? '← Voltar ao início' : '← Back home'}</Link>
        </div>
      </div>
    );
  }

  const cSlug = slugify(county.name);
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <OrthocareHeroSearch lang={lang} hero={{ badge: county.name, h1: c.h1, heroP: c.subtitle }} />

      <section className="max-w-5xl mx-auto px-6 py-12">
        <p className="text-slate-700 leading-relaxed text-lg">{c.intro}</p>

        <div className="grid md:grid-cols-2 gap-4 my-10">
          {[
            { icon: Truck, t: lang === 'pt' ? 'Entrega 24h' : '24h delivery', d: lang === 'pt' ? `Em ${county.name}` : `In ${county.name}` },
            { icon: Clock, t: lang === 'pt' ? '7 dias/semana' : '7 days/week', d: lang === 'pt' ? 'Incluindo fins de semana' : 'Including weekends' },
            { icon: Shield, t: lang === 'pt' ? 'Certificado e higienizado' : 'Certified & sanitized', d: 'CE • NIF 515948039' },
            { icon: CheckCircle, t: lang === 'pt' ? 'Reserva 100% online' : '100% online booking', d: 'orthocare.pt' },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-4">
              <f.icon className="w-6 h-6 text-[#0a3452] shrink-0 mt-0.5" />
              <div><p className="font-semibold text-slate-800">{f.t}</p><p className="text-sm text-slate-500">{f.d}</p></div>
            </div>
          ))}
        </div>

        <h2 className="text-2xl font-bold text-slate-800 mb-4">{c.equipTitle}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
          {COUNTY_EQUIPMENT.map((eq) => (
            <Link key={eq.slug} to={toSitePath(equipmentUrl(lang, eq.slug, cSlug))}
              className="flex items-center justify-between bg-white border border-slate-200 hover:border-[#0a3452]/40 hover:shadow-sm rounded-xl px-4 py-3 transition-all">
              <span className="font-semibold text-slate-800">{eq.labels[lang] || eq.labels.pt}</span>
              <span className="text-[#0a3452] text-sm font-medium">{lang === 'pt' ? 'Ver →' : 'View →'}</span>
            </Link>
          ))}
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-10">
          <p className="text-sm text-slate-600 leading-relaxed">{c.keyword}</p>
        </div>

        <div className="space-y-4 mb-12">
          {c.faqs.map((f, i) => (
            <details key={i} className="bg-white border border-slate-200 rounded-xl p-5 group">
              <summary className="font-semibold text-slate-800 cursor-pointer list-none flex justify-between items-center">
                {f.q}<span className="text-[#0a3452] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>

        <RealReviewsSection lang={lang} />
      </section>

      <OrthocareFooter language={lang} />
      <ComplaintsBookButton />
    </div>
  );
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
  el.setAttribute('content', content);
}
function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
  el.setAttribute('href', href);
}
function setHreflang(langUrls) {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
  const localeMap = {
    pt: ['pt-PT', 'pt'],
    en: ['en-GB', 'en-US', 'en-IE', 'en'],
    es: ['es-ES', 'es'],
    fr: ['fr-FR', 'fr-BE', 'fr'],
    de: ['de-DE', 'de-AT', 'de-CH', 'de'],
    it: ['it-IT', 'it'],
    nl: ['nl-NL', 'nl-BE', 'nl'],
  };
  const entries = [];
  for (const [code, href] of Object.entries(langUrls)) {
    for (const locale of (localeMap[code] || [code])) {
      entries.push({ hreflang: locale, href });
    }
  }
  entries.push({ hreflang: 'x-default', href: langUrls.en || langUrls.pt });
  entries.forEach(({ hreflang, href }) => {
    const l = document.createElement('link');
    l.rel = 'alternate'; l.hreflang = hreflang; l.href = href;
    l.setAttribute('data-page-hreflang', '1');
    document.head.appendChild(l);
  });
}
function injectSchema(id, obj) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('script'); el.type = 'application/ld+json'; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(obj);
}